import React from "react";

import { NextSeo } from "next-seo";

import type { ApplyCohortYup, ApplyProgramYup, ApplyStepYup } from "features/program-applications/utils";

import { Heading } from "../heading";

interface ApplyPageProps {
  backButton?: React.ReactNode;
  header?: React.ReactNode;
  title?: string;
  seoTitle?: string;
  description?: string;
  program?: ApplyProgramYup;
  slug?: string;
  cohort?: ApplyCohortYup;
  step?: ApplyStepYup;
  thin?: boolean;
}

interface OpenGraphImage {
  url: string;
  width: number;
  height: number;
  alt: string;
}

interface OpenGraphOverride {
  type: string;
  locale: string;
  url: string;
  site_name: string;
  description: string;
  images: OpenGraphImage[];
}

interface SeoOverrides {
  title?: string;
  openGraph?: OpenGraphOverride;
}

export const ApplyPage: React.FC<React.PropsWithChildren<Readonly<ApplyPageProps>>> = ({
  children,
  title,
  description,
  header,
  backButton,
  seoTitle,
  program,
  thin = false,
}) => {
  const seo: SeoOverrides = {};

  if (seoTitle || title) {
    seo.title = seoTitle || title;
  }
  if (program) {
    seo.openGraph = {
      type: "website",
      locale: "en_US",
      url: `https://apply.beondeck.com/programs/${program.slug}`,
      site_name: `Apply to the On Deck ${program.name} Fellowship`,
      description:
        "On Deck is where top talent comes to accelerate their ideas and careers, surrounded by a world-class community.",
      images: [
        {
          url:
            program?.opengraphImageUrl ||
            "https://assets-global.website-files.com/5fdbf0943c2eae424a7cde2c/6001e43d4bff6777d474b57e_Group%20980.png",
          width: 1200,
          height: 630,
          alt: program.name,
        },
      ],
    };
  }
  return (
    <>
      {header}
      <div className={`px-4 pb-6 mx-auto max-w-7xl sm:px-6 lg:px-8 ${header ? "-mt-24" : ""}`}>
        <div className={`${thin ? "max-w-xl" : "max-w-4xl"} pt-12 m-auto`}>
          {!header && (
            <>
              {!!backButton && backButton}
              {(seoTitle || title) && <NextSeo {...seo} />}

              {title && <Heading text={title} className="my-8" />}

              {description && <h2 className="max-w-xl mx-auto text-lg text-center text-gray-500">{description}</h2>}
            </>
          )}

          {children}
        </div>
      </div>
    </>
  );
};
